import type {
  PiMatrixAgentChangeSet,
  PiMatrixAgentPreview,
  PiMatrixAgentPreviewEnvelope,
  PiMatrixAgentWindow,
  PiMatrixAgentWindowRow,
  PiMatrixAgentWitnessRef,
} from '../types/piMatrixAgent'

const mergedWindow = (
  changeSet: PiMatrixAgentChangeSet,
  side: 'before' | 'after',
): PiMatrixAgentWindow | undefined => {
  const witnesses = new Map<string, PiMatrixAgentWitnessRef>()
  const rows = new Map<string, PiMatrixAgentWindowRow>()
  changeSet.steps.forEach(step => {
    const window = step[side]
    if (!window) return
    window.witnesses.forEach(witness => witnesses.set(witness.id, witness))
    window.rows.forEach(row => {
      if (side === 'before' && rows.has(row.id)) return
      rows.set(row.id, row)
    })
  })
  const orderedRows = Array.from(rows.values()).sort((left, right) => left.index - right.index)
  if (orderedRows.length === 0) return undefined
  return {
    rowStart: Math.min(...orderedRows.map(row => row.index)),
    rowEnd: Math.max(...orderedRows.map(row => row.index)),
    witnesses: Array.from(witnesses.values()),
    rows: orderedRows,
  }
}

export const buildPiMatrixAgentChangeSetPreview = (
  changeSet: PiMatrixAgentChangeSet,
): { preview: PiMatrixAgentPreview; envelope: PiMatrixAgentPreviewEnvelope } => {
  const preview: PiMatrixAgentPreview = {
    previewId: changeSet.changeSetId,
    baseSnapshotId: changeSet.baseSnapshotId,
    operation: { kind: 'agent_change_set', stepCount: changeSet.steps.length },
    affectedRowIds: changeSet.affectedRowIds,
    affectedWitnessIds: changeSet.affectedWitnessIds,
    invariants: changeSet.invariants,
    summary: `${changeSet.steps.length} edit${changeSet.steps.length === 1 ? '' : 's'} ready for one approval`,
  }
  return {
    preview,
    envelope: {
      preview,
      before: mergedWindow(changeSet, 'before'),
      after: mergedWindow(changeSet, 'after'),
      idRemap: changeSet.steps.flatMap(step => step.idRemap),
      unassignedFragments: changeSet.steps.flatMap(step => step.unassignedFragments),
    },
  }
}
